import React from 'react';
import Between from 'between.js';
import Easing from 'easing-functions';
import './Styles/Confetti.css';

// counter for utils & constants
var COUNT = 1;

const RANDOM_DELTA = 1; // adds some random movement direction per particle

// utilities (avoiding lodash dependency)
function getRandom(low, high) {
  return Math.random() * Math.abs(high - low) + low;
}

function getUniqueId() {
  return COUNT++;
}

/**
 * Component that shoots confetti upwards from the middle of the screen, then fades to black
 * Based on the codepen: https://codepen.io/jscottsmith/pen/VjPaLO
 * but removed interactivity & dependencies on GSAP & lodash. Using the much lighter between.js instead for animation
 *
 * Optional props:
 * - confettiDone: callback function called when all is over
 * - relPos: {x, y} relative position for emitter. default {.5, .5}
 * - fadeToColor: {r, g, b} color for fade-out. default {0, 0, 0}
 * - particleCount: default 75
 */
export class Confetti extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      confettiDone: props.confettiDone || (() => {}),
      relPos: props.relPos || { x: 0.5, y: 0.5 },
      fadeToColor: props.fadeToColor || { r: 0, g: 0, b: 0 },
      particleCount: props.particleCount || 75
    };
  }

  componentDidMount() {
    // setup a canvas
    this.canvas = document.getElementById('confetti');
    this.dpr = window.devicePixelRatio || 1;
    this.ctx = this.canvas.getContext('2d');
    this.ctx.scale(this.dpr, this.dpr);

    this.colors = ['#ff5964', '#58b0ae', '#efbf62', '#94b55d'];

    // add confetti here
    this.confettiSpriteIds = [];
    this.confettiSprites = {};

    this.drawConfetti = this.drawConfetti.bind(this);

    this.setCanvasSize();
    this.addConfettiParticles(
      this.state.particleCount,
      window.innerWidth * this.state.relPos.x * this.dpr,
      window.innerHeight * this.state.relPos.y * this.dpr
    );
    this.tweenAlpha();
    this.frame = requestAnimationFrame(this.drawConfetti);
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.timeout);
    this.confettiSpriteIds.forEach(id => {
      delete this.confettiSprites[id];
    });
    this.confettiSpriteIds.length = 0;
  }

  render() {
    return <canvas id="confetti" />;
  }

  setCanvasSize = function() {
    this.canvas.width = window.innerWidth * this.dpr;
    this.canvas.height = window.innerHeight * this.dpr;
    this.canvas.style.width = window.innerWidth + 'px';
    this.canvas.style.height = window.innerHeight + 'px';
  };

  addConfettiParticles = function(amount, x, y) {
    for (let i = 0; i < amount; i++) {
      // sprite (I blame the original author for all the magic numbers :)
      const r = getRandom(2, 12) * this.dpr;
      const d = getRandom(5, 50) * this.dpr;

      const color = this.colors[Math.round(Math.random() * this.colors.length)];

      const tilt = getRandom(10, -10);
      const tiltAngleIncremental = getRandom(0.07, 0.05);
      const tiltAngle = 0;

      // add in some personality for each confetti
      const xRand = getRandom(-RANDOM_DELTA, RANDOM_DELTA);
      const yRand = getRandom(-RANDOM_DELTA, RANDOM_DELTA);

      const id = getUniqueId();

      const sprite = {
        [id]: {
          x,
          y,
          xRand,
          yRand,
          r,
          d,
          color,
          tilt,
          tiltAngleIncremental,
          tiltAngle
        }
      };

      Object.assign(this.confettiSprites, sprite);
      this.confettiSpriteIds.push(id);
      this.tweenConfettiParticle(id);
    }
  };

  tweenAlpha() {
    this.alpha = 0;
    new Between(0, 1)
      .time(3000)
      .on('update', value => {
        this.alpha = value;
      })
      .on('complete', () => {
        this.timeout = setTimeout(() => {
          this.state.confettiDone();
          cancelAnimationFrame(this.frame);
        }, 500);
      });
  }

  tweenConfettiParticle = function(id) {
    const sprite = this.confettiSprites[id];
    const spread = Math.min(300, window.innerWidth / 1.5);
    sprite.dx = (getRandom(-spread, spread) / 15) * this.dpr;
    sprite.dy = (getRandom(50, 450) / 15) * this.dpr;

    // between.js doesn't manipulate its inputs so might as well pass in new objects
    new Between({ dx: sprite.dx, dy: sprite.dy }, { dx: 0, dy: -5 * this.dpr })
      .time(500)
      .easing(Easing.Cubic.InOut)
      .on('update', value => {
        sprite.dx = value.dx;
        sprite.dy = value.dy;
      });
  };

  updateConfettiParticle = function(id) {
    const sprite = this.confettiSprites[id];

    // wobbly sprites
    const tiltAngle = 0.0005 * sprite.d;
    sprite.tiltAngle += tiltAngle;
    sprite.tiltAngle += sprite.tiltAngleIncremental;

    // shooting then falling sprites
    sprite.y -= sprite.dy + sprite.yRand;
    sprite.x += sprite.dx + sprite.xRand;

    sprite.tilt = Math.sin(sprite.tiltAngle - sprite.r / 2) * sprite.r * 2;
  };

  drawConfetti = function() {
    if (this.confettiSpriteIds.length === 0) {
      return;
    }

    // clearing screen
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // drawing confettis
    this.confettiSpriteIds.forEach(id => {
      const sprite = this.confettiSprites[id];

      this.ctx.beginPath();
      this.ctx.lineWidth = sprite.d / 2;
      this.ctx.strokeStyle = sprite.color;
      this.ctx.moveTo(sprite.x + sprite.tilt + sprite.r, sprite.y);
      this.ctx.lineTo(
        sprite.x + sprite.tilt,
        sprite.y + sprite.tilt + sprite.r
      );
      this.ctx.stroke();
      this.ctx.closePath();

      this.updateConfettiParticle(id);
    });

    // drawing fade-out screen
    this.ctx.fillStyle = `rgba(${this.state.fadeToColor.r}, ${
      this.state.fadeToColor.g
    }, ${this.state.fadeToColor.b}, ${this.alpha})`;
    this.ctx.fillRect(
      0,
      0,
      window.innerWidth * this.dpr,
      window.innerHeight * this.dpr
    );

    this.frame = requestAnimationFrame(this.drawConfetti);
  };
}
